const ATTRIBUTION_SOURCE_QUERY_KEYS = ['utm_source', 'source'] as const;

const IGNORED_REFERRER_HOSTS = new Set(['accounts.google.com']);

function normalizeText(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeHostname(value: string | null | undefined): string | null {
  const normalized = normalizeText(value)?.toLowerCase() ?? null;
  if (!normalized) return null;
  return normalized.endsWith('.') ? normalized.slice(0, -1) : normalized;
}

function parseReferrerUrl(referrer: string): URL | null {
  try {
    return new URL(referrer);
  } catch {
    if (referrer.includes('://') || referrer.startsWith('/')) {
      return null;
    }
    if (!referrer.includes('.') || /\s/.test(referrer)) {
      return null;
    }

    try {
      return new URL(`https://${referrer}`);
    } catch {
      return null;
    }
  }
}

function resolveHostFromParsedUrl(url: URL): string | null {
  const host = normalizeHostname(url.hostname);
  if (!host) return null;
  if (IGNORED_REFERRER_HOSTS.has(host)) return null;
  return host;
}

export function resolveAttributionSourceFromSearchParams(
  searchParams: URLSearchParams | null | undefined
): string | null {
  if (!searchParams) return null;
  for (const key of ATTRIBUTION_SOURCE_QUERY_KEYS) {
    const value = normalizeText(searchParams.get(key));
    if (value) return value;
  }
  return null;
}

export function resolveAttributionReferrerHost(
  referrer: string | null | undefined
): string | null {
  const normalizedReferrer = normalizeText(referrer);
  if (!normalizedReferrer) return null;

  const parsed = parseReferrerUrl(normalizedReferrer);
  if (!parsed) return null;

  return resolveHostFromParsedUrl(parsed);
}

export function resolveAttributionSourceFromReferrer(
  referrer: string | null | undefined
): string | undefined {
  const normalizedReferrer = normalizeText(referrer);
  if (!normalizedReferrer) return undefined;

  const parsed = parseReferrerUrl(normalizedReferrer);
  if (parsed) {
    const sourceFromQuery = resolveAttributionSourceFromSearchParams(
      parsed.searchParams
    );
    if (sourceFromQuery) return sourceFromQuery;

    return resolveHostFromParsedUrl(parsed) ?? undefined;
  }

  if (IGNORED_REFERRER_HOSTS.has(normalizeHostname(normalizedReferrer) ?? '')) {
    return undefined;
  }

  // Preserve historical behavior for malformed referrer values.
  return normalizedReferrer;
}
